import api from 'api';
import { errorMessageFactory } from 'factories/api';
import { getIsDemoManagerPresetActive } from 'redux/modules/tic/tic.demo.selectors';
import { showToast } from 'redux/modules/toasts.actions';
import { isDemoUserId } from 'redux/modules/users-page/demo/usersPage.demo.constants';
import { DEMO_MODE_BLOCKED_MESSAGE } from 'redux/modules/users-page/demo/usersPage.demo.messages';
import { resetQueryUsers } from 'redux/modules/users-page/users-list/usersList.actions';
import { showUnoccupiedSeatsWarningModal } from 'redux/modules/users-page/usersPage.actions';
import { xhrStatusQueryInfo } from 'redux/reusable/xhrStatus';
import type { Thunk } from 'types/redux';
import type { User } from 'types/user';
import { fetchPaginated } from 'utils/api';
import { uniqBy } from 'utils/typesafe';
import * as types from './users.types';
import { usersXhrModule } from './users.types';

const { wrapFetching } = usersXhrModule;

export const addUser = types.addUser;
export const removeUser = types.removeUser;
export const updateUser = types.updateUser;

export function fetchUsersSmartly(
  type: 'active' | 'deleted' = 'active',
  force?: boolean
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const fetchKey = `users-${type}`;
    const xhrStatus = xhrStatusQueryInfo(state.users.xhrStatus, { key: fetchKey });

    if (!xhrStatus.shouldFetch && !force) {
      return;
    }
    const perPage = 500;
    const users = await dispatch(
      wrapFetching(
        fetchPaginated<User>({ perPage }, page => api.getUsers({ type }, { page, perPage })),
        fetchKey
      )
    );

    // There is a possibility of getting duplicate users
    const uniqueUsers = uniqBy(users, 'id');
    if (uniqueUsers.length !== users.length) {
      console.warn(
        'Duplicate users returned from API, possible caching issue (fethed: %d, unique: %d)',
        users.length,
        uniqueUsers.length
      );
    }

    dispatch(types.fetchedPartial(uniqueUsers));
  };
}

export function reloadUsers(type = 'active'): Thunk<Promise<void>> {
  return async dispatch => {
    const fetchKey = `users-${type}`;
    const perPage = 500;
    const users = await dispatch(
      wrapFetching(
        fetchPaginated<User>({ perPage }, page => api.getUsers({ type }, { page, perPage })),
        fetchKey
      )
    );

    dispatch(types.fetched(users));
  };
}

export function fetchUserById(userId: number): Thunk<Promise<User>> {
  return async dispatch => {
    const user = await api.getUserById(userId);
    dispatch(updateUser(user));
    return user;
  };
}

export function deleteUserById(userId: number): Thunk {
  return async (dispatch, getState) => {
    if (isDemoUserId(userId) && getIsDemoManagerPresetActive(getState())) {
      dispatch(showToast({ message: DEMO_MODE_BLOCKED_MESSAGE, type: 'info' }));
      return;
    }
    try {
      const result = await api.deleteUser(userId);

      dispatch(removeUser(userId));
      dispatch(resetQueryUsers());

      dispatch(showUnoccupiedSeatsWarningModal());

      return result;
    } catch (err) {
      dispatch(
        showToast({
          message: `Could not removed user with id ${userId}, ${err.message}`,
          type: 'danger',
        })
      );
    }
  };
}

export function sendLogHoursRequestNotification(user: { id: number; name: string }): Thunk {
  return dispatch => {
    return api
      .sendLogHoursRequestNotification(user.id)
      .then(() => {
        dispatch(
          showToast({
            message: `Notification sent to user ${user.name}`,
            type: 'success',
          })
        );
      })
      .catch(error => {
        dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
      });
  };
}

export function searchUsers(query: string): Thunk {
  return async dispatch => {
    const results = await api.searchUsers(query);
    dispatch(types.fetchedSearchResults(results));
  };
}
export function clearUserSearchResults() {
  return types.clearSearchResults();
}

export function reInviteUser(id: number): Thunk {
  return async (dispatch, getState) => {
    if (isDemoUserId(id) && getIsDemoManagerPresetActive(getState())) {
      dispatch(showToast({ message: DEMO_MODE_BLOCKED_MESSAGE, type: 'info' }));
      return;
    }
    try {
      await api.reInviteUser(id);
    } catch (error) {
      dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
    }
  };
}
